import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Plus, Edit2, Trash2, Users, Clock, User, Check, X,
  GraduationCap, ChevronDown, Search, UserPlus, MessageCircle, Send,
  CheckCheck, ArrowLeft, AlertTriangle, TrendingUp
} from 'lucide-react';
import api from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { useToast } from '../../hooks/use-toast';
import { useAuth } from '../../context/AuthContext';
import AdminLayout from './AdminLayout';

const AdminGroupes = () => {
  const { user } = useAuth();
  const [groupes, setGroupes] = useState([]);
  const [groupesStats, setGroupesStats] = useState(null);
  const [eleves, setEleves] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    nom: '',
    description: '',
    niveau: '',
    horaire: '',
    capacite_max: 12,
    annee_scolaire: '2025-2026'
  });
  const [selectedGroupe, setSelectedGroupe] = useState(null);
  const [groupeMembers, setGroupeMembers] = useState([]);
  const [activeTab, setActiveTab] = useState('members'); // 'members' or 'chat'
  const [chatMessages, setChatMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [targetMembers, setTargetMembers] = useState([]); // For specific member messaging
  const [eleveSearchQuery, setEleveSearchQuery] = useState('');
  const messagesEndRef = useRef(null);
  const { toast } = useToast();

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (selectedGroupe && activeTab === 'chat') {
      loadChatMessages(selectedGroupe.id);
    }
  }, [selectedGroupe, activeTab]);

  useEffect(() => {
    scrollToBottom();
  }, [chatMessages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadData = async () => {
    try {
      const [groupesRes, elevesRes, statsRes] = await Promise.all([
        api.get('/groupes?with_stats=true'),
        api.get('/users/eleves'),
        api.get('/groupes/stats/overview')
      ]);
      setGroupes(groupesRes.data);
      setEleves(elevesRes.data);
      setGroupesStats(statsRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadGroupeMembers = async (groupeId) => {
    try {
      const res = await api.get(`/groupes/${groupeId}/members`);
      setGroupeMembers(res.data);
    } catch (error) {
      console.error('Error loading members:', error);
    }
  };

  const loadChatMessages = async (groupeId) => {
    try {
      const res = await api.get(`/groupes/${groupeId}/chat`);
      setChatMessages(res.data);
    } catch (error) {
      console.error('Error loading chat:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await api.put(`/groupes/${editingId}`, formData);
        toast({ title: 'Groupe mis à jour' });
      } else {
        await api.post('/groupes', formData);
        toast({ title: 'Groupe créé avec succès' });
      }
      resetForm();
      loadData();
    } catch (error) {
      console.error('Error:', error);
      toast({ title: 'Erreur', description: error.response?.data?.detail || 'Une erreur est survenue', variant: 'destructive' });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce groupe ?')) return;
    try {
      await api.delete(`/groupes/${id}`);
      toast({ title: 'Groupe supprimé' });
      loadData();
      if (selectedGroupe?.id === id) setSelectedGroupe(null);
    } catch (error) {
      toast({ title: 'Erreur', description: 'Impossible de supprimer', variant: 'destructive' });
    }
  };

  const assignEleveToGroupe = async (userId, groupeId) => {
    try {
      await api.put(`/admin/users/${userId}/assign-groupe`, null, {
        params: { groupe_id: groupeId }
      });
      toast({ title: 'Élève affecté au groupe' });
      loadData();
      if (selectedGroupe) loadGroupeMembers(selectedGroupe.id);
    } catch (error) {
      toast({ title: 'Erreur', variant: 'destructive' });
    }
  };

  const sendChatMessage = async () => {
    if (!newMessage.trim() || !selectedGroupe) return;
    
    setSendingMessage(true);
    try {
      await api.post(`/groupes/${selectedGroupe.id}/chat`, {
        content: newMessage.trim(),
        target_members: targetMembers
      });
      setNewMessage('');
      setTargetMembers([]);
      loadChatMessages(selectedGroupe.id);
    } catch (error) {
      toast({ title: 'Erreur', description: 'Impossible d\'envoyer le message', variant: 'destructive' });
    } finally {
      setSendingMessage(false);
    }
  };

  const resetForm = () => {
    setShowForm(false);
    setEditingId(null);
    setFormData({ nom: '', description: '', niveau: '', horaire: '', capacite_max: 12, annee_scolaire: '2025-2026' });
  };

  const handleEdit = (groupe) => {
    setEditingId(groupe.id);
    setFormData({
      nom: groupe.nom,
      description: groupe.description || '',
      niveau: groupe.niveau || '',
      horaire: groupe.horaire || '',
      capacite_max: groupe.capacite_max || 12,
      annee_scolaire: groupe.annee_scolaire || '2025-2026'
    });
    setShowForm(true);
  };

  // Helper function to get fill rate color
  const getFillRateColor = (taux) => {
    if (taux >= 90) return { bg: 'bg-red-500', text: 'text-red-600', bgLight: 'bg-red-100' };
    if (taux >= 70) return { bg: 'bg-yellow-500', text: 'text-yellow-600', bgLight: 'bg-yellow-100' };
    return { bg: 'bg-green-500', text: 'text-green-600', bgLight: 'bg-green-100' };
  };

  const selectGroupe = (groupe) => {
    setSelectedGroupe(groupe);
    loadGroupeMembers(groupe.id);
    setActiveTab('members');
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
  };

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-groupes">
        <div className="flex justify-between items-center">
          <h1 className="text-2xl font-bold text-[#2C3C65]">Groupes / Classes</h1>
          <Button onClick={() => setShowForm(true)} className="bg-[#285BBB] hover:bg-[#2C3C65]">
            <Plus className="h-4 w-4 mr-2" />
            Nouveau groupe
          </Button>
        </div>

        {/* Form Modal */}
        <AnimatePresence>
          {showForm && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
              onClick={(e) => e.target === e.currentTarget && resetForm()}
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                className="bg-white rounded-xl shadow-xl w-full max-w-lg p-6"
              >
                <h2 className="text-xl font-bold text-[#2C3C65] mb-4">
                  {editingId ? 'Modifier le groupe' : 'Nouveau groupe'}
                </h2>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-[#2C3C65] mb-1">Nom du groupe *</label>
                    <Input
                      value={formData.nom}
                      onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                      required
                      placeholder="Ex: Cambridge Starters - Mercredi"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-[#2C3C65] mb-1">Niveau</label>
                      <select
                        value={formData.niveau}
                        onChange={(e) => setFormData({ ...formData, niveau: e.target.value })}
                        className="w-full border rounded-lg p-2"
                      >
                        <option value="">Sélectionner...</option>
                        <option value="Kiddoes">Kiddoes (4-6 ans)</option>
                        <option value="Pre A1 - Starters">Pre A1 - Starters</option>
                        <option value="A1 - Movers">A1 - Movers</option>
                        <option value="A2 - Flyers">A2 - Flyers</option>
                        <option value="A2 - Key">A2 - Key</option>
                        <option value="B1 - Preliminary">B1 - Preliminary</option>
                        <option value="Youth - Prépa B1/B2">Youth - Prépa B1/B2</option>
                        <option value="B2 - First">B2 - First</option>
                        <option value="C1 - Advanced">C1 - Advanced</option>
                        <option value="C2 - Proficiency">C2 - Proficiency</option>
                        <option value="Adultes">Adultes</option>
                        <option value="Entreprises">Entreprises</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#2C3C65] mb-1">Année scolaire</label>
                      <Input
                        value={formData.annee_scolaire}
                        onChange={(e) => setFormData({ ...formData, annee_scolaire: e.target.value })}
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#2C3C65] mb-1">Horaire</label>
                    <Input
                      value={formData.horaire}
                      onChange={(e) => setFormData({ ...formData, horaire: e.target.value })}
                      placeholder="Ex: Mercredi 09h00 - 10h30"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#2C3C65] mb-1">Capacité maximale</label>
                    <Input
                      type="number"
                      min="1"
                      max="30"
                      value={formData.capacite_max}
                      onChange={(e) => setFormData({ ...formData, capacite_max: parseInt(e.target.value) || 12 })}
                      placeholder="12"
                    />
                    <p className="text-xs text-gray-400 mt-1">Nombre maximum d'élèves dans ce groupe</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#2C3C65] mb-1">Description</label>
                    <Textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      rows={2}
                      placeholder="Informations complémentaires sur le groupe..."
                    />
                  </div>
                  <div className="flex justify-end space-x-3 pt-4">
                    <Button type="button" variant="outline" onClick={resetForm}>
                      Annuler
                    </Button>
                    <Button type="submit" className="bg-[#285BBB] hover:bg-[#2C3C65]">
                      {editingId ? 'Mettre à jour' : 'Créer le groupe'}
                    </Button>
                  </div>
                </form>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Stats Overview */}
        {groupesStats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-xl shadow-md p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Users className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-[#2C3C65]">{groupesStats.total_groupes}</p>
                  <p className="text-xs text-[#6777A1]">Groupes actifs</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl shadow-md p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <User className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-[#2C3C65]">{groupesStats.total_eleves}</p>
                  <p className="text-xs text-[#6777A1]">Élèves inscrits</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl shadow-md p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <TrendingUp className="h-5 w-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-[#2C3C65]">{groupesStats.taux_moyen}%</p>
                  <p className="text-xs text-[#6777A1]">Taux moyen</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl shadow-md p-4">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 ${groupesStats.groupes_critiques?.length > 0 ? 'bg-red-100' : 'bg-gray-100'} rounded-lg flex items-center justify-center`}>
                  <AlertTriangle className={`h-5 w-5 ${groupesStats.groupes_critiques?.length > 0 ? 'text-red-600' : 'text-gray-400'}`} />
                </div>
                <div>
                  <p className="text-2xl font-bold text-[#2C3C65]">{groupesStats.groupes_critiques?.length || 0}</p>
                  <p className="text-xs text-[#6777A1]">Groupes pleins (≥90%)</p>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Groupes List */}
          <div className="lg:col-span-1 bg-white rounded-xl shadow-md overflow-hidden">
            <div className="p-4 border-b bg-gray-50">
              <h2 className="font-semibold text-[#2C3C65]">Groupes ({groupes.length})</h2>
            </div>
            {loading ? (
              <div className="p-8 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#285BBB] mx-auto"></div>
              </div>
            ) : groupes.length === 0 ? (
              <div className="p-8 text-center">
                <Users className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                <p className="text-[#6777A1]">Aucun groupe</p>
                <p className="text-sm text-gray-400 mt-1">Créez votre premier groupe</p>
              </div>
            ) : (
              <div className="divide-y max-h-[500px] overflow-y-auto">
                {groupes.map((groupe) => {
                  const taux = groupe.taux_remplissage || 0;
                  const colors = getFillRateColor(taux);
                  const elevesCount = groupe.eleves_count || 0;
                  const capacite = groupe.capacite_max || 12;
                  
                  return (
                    <div
                      key={groupe.id}
                      onClick={() => selectGroupe(groupe)}
                      className={`p-4 cursor-pointer hover:bg-gray-50 transition-colors ${
                        selectedGroupe?.id === groupe.id ? 'bg-blue-50 border-l-4 border-[#285BBB]' : ''
                      }`}
                    >
                      <div className="flex justify-between items-start mb-2">
                        <div className="flex-1">
                          <p className="font-medium text-[#2C3C65]">{groupe.nom}</p>
                          <p className="text-sm text-[#6777A1]">{groupe.niveau}</p>
                          <p className="text-xs text-[#6777A1] mt-1">{groupe.horaire}</p>
                        </div>
                        <div className="flex space-x-1">
                          <button onClick={(e) => { e.stopPropagation(); handleEdit(groupe); }} className="p-1 text-gray-400 hover:text-[#285BBB]">
                            <Edit2 className="h-4 w-4" />
                          </button>
                          <button onClick={(e) => { e.stopPropagation(); handleDelete(groupe.id); }} className="p-1 text-gray-400 hover:text-red-500">
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                      {/* Fill Rate Bar */}
                      <div className="mt-2">
                        <div className="flex justify-between items-center mb-1">
                          <span className={`text-xs font-medium ${colors.text}`}>
                            {elevesCount}/{capacite} élèves
                          </span>
                          <span className={`text-xs font-semibold ${colors.text}`}>
                            {taux}%
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className={`${colors.bg} h-2 rounded-full transition-all duration-300`}
                            style={{ width: `${Math.min(taux, 100)}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Groupe Details */}
          <div className="lg:col-span-2">
            {selectedGroupe ? (
              <div className="space-y-6">
                {/* Groupe Info */}
                <div className="bg-white rounded-xl shadow-md p-6">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h2 className="text-xl font-bold text-[#2C3C65]">{selectedGroupe.nom}</h2>
                      <p className="text-[#6777A1]">{selectedGroupe.niveau} - {selectedGroupe.annee_scolaire}</p>
                    </div>
                    {(() => {
                      const taux = selectedGroupe.taux_remplissage || 0;
                      const colors = getFillRateColor(taux);
                      return (
                        <span className={`px-3 py-1 ${colors.bgLight} ${colors.text} text-sm font-semibold rounded-full`}>
                          {groupeMembers.length}/{selectedGroupe.capacite_max || 12} élèves ({taux}%)
                        </span>
                      );
                    })()}
                  </div>
                  {/* Fill Rate Progress */}
                  {(() => {
                    const taux = selectedGroupe.taux_remplissage || 0;
                    const colors = getFillRateColor(taux);
                    return (
                      <div className="mb-4">
                        <div className="flex justify-between items-center mb-1">
                          <span className="text-sm text-[#6777A1]">Taux de remplissage</span>
                          <span className={`text-sm font-semibold ${colors.text}`}>{taux}%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-3">
                          <div 
                            className={`${colors.bg} h-3 rounded-full transition-all duration-500`}
                            style={{ width: `${Math.min(taux, 100)}%` }}
                          />
                        </div>
                        <p className="text-xs text-gray-400 mt-1">
                          {(selectedGroupe.capacite_max || 12) - groupeMembers.length} place(s) disponible(s)
                        </p>
                      </div>
                    );
                  })()}
                  <div className="bg-[#F5F7FF] p-3 rounded-lg">
                    <p className="text-sm text-[#6777A1]">Horaire</p>
                    <p className="font-medium text-[#2C3C65]">{selectedGroupe.horaire || 'Non défini'}</p>
                  </div>
                </div>

                {/* Tabs */}
                <div className="bg-white rounded-xl shadow-md overflow-hidden">
                  <div className="flex border-b">
                    <button
                      onClick={() => setActiveTab('members')}
                      className={`flex-1 py-3 px-4 text-sm font-medium flex items-center justify-center gap-2 transition-colors ${
                        activeTab === 'members' 
                          ? 'text-[#285BBB] border-b-2 border-[#285BBB] bg-blue-50' 
                          : 'text-[#6777A1] hover:bg-gray-50'
                      }`}
                    >
                      <Users className="h-4 w-4" />
                      Membres
                    </button>
                    <button
                      onClick={() => setActiveTab('chat')}
                      className={`flex-1 py-3 px-4 text-sm font-medium flex items-center justify-center gap-2 transition-colors ${
                        activeTab === 'chat' 
                          ? 'text-[#285BBB] border-b-2 border-[#285BBB] bg-blue-50' 
                          : 'text-[#6777A1] hover:bg-gray-50'
                      }`}
                    >
                      <MessageCircle className="h-4 w-4" />
                      Discussion du groupe
                    </button>
                  </div>

                  {activeTab === 'members' ? (
                    <div className="p-6">
                      <h3 className="text-lg font-semibold text-[#2C3C65] mb-4">Élèves du groupe ({groupeMembers.length}/{selectedGroupe.capacite_max || 12})</h3>
                      {groupeMembers.length === 0 ? (
                        <p className="text-[#6777A1] text-center py-4">Aucun élève dans ce groupe</p>
                      ) : (
                        <div className="space-y-2 mb-6">
                          {groupeMembers.map((member) => (
                            <div key={member.id} className="flex items-center justify-between p-3 bg-[#F5F7FF] rounded-lg">
                              <div className="flex items-center">
                                <div className="w-10 h-10 bg-[#285BBB] rounded-full flex items-center justify-center text-white font-semibold mr-3">
                                  {member.prenom?.[0]}{member.nom?.[0]}
                                </div>
                                <div>
                                  <p className="font-medium text-[#2C3C65]">{member.prenom} {member.nom}</p>
                                  <p className="text-sm text-[#6777A1]">{member.email}</p>
                                </div>
                              </div>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={async () => {
                                  if (window.confirm(`Retirer ${member.prenom} ${member.nom} du groupe ?`)) {
                                    try {
                                      await api.delete(`/users/eleves/${member.id}/groupe`);
                                      toast({ title: 'Élève retiré du groupe' });
                                      loadGroupeMembers(selectedGroupe.id);
                                      loadData();
                                    } catch (error) {
                                      toast({ title: 'Erreur', variant: 'destructive' });
                                    }
                                  }
                                }}
                                className="text-red-500 border-red-200 hover:bg-red-50"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Add Students */}
                      <h3 className="text-lg font-semibold text-[#2C3C65] mb-4 pt-4 border-t">Ajouter des élèves</h3>
                      <div className="mb-4">
                        <div className="relative">
                          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                          <Input
                            placeholder="Rechercher un élève..."
                            className="pl-10"
                            value={eleveSearchQuery}
                            onChange={(e) => setEleveSearchQuery(e.target.value)}
                          />
                        </div>
                      </div>
                      <div className="space-y-2 max-h-60 overflow-y-auto">
                        {eleves
                          .filter(e => e.groupe_id !== selectedGroupe.id)
                          .filter(e => {
                            if (!eleveSearchQuery) return true;
                            const search = eleveSearchQuery.toLowerCase();
                            return e.nom?.toLowerCase().includes(search) || 
                                   e.prenom?.toLowerCase().includes(search) ||
                                   e.email?.toLowerCase().includes(search);
                          })
                          .map((eleve) => (
                          <div key={eleve.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <div className="flex items-center">
                              <div className="w-10 h-10 bg-gray-400 rounded-full flex items-center justify-center text-white font-semibold mr-3">
                                {eleve.prenom?.[0]}{eleve.nom?.[0]}
                              </div>
                              <div>
                                <p className="font-medium text-[#2C3C65]">{eleve.prenom} {eleve.nom}</p>
                                <p className="text-sm text-[#6777A1]">
                                  {eleve.groupe ? `Actuellement: ${eleve.groupe.nom}` : 'Non affecté'}
                                </p>
                              </div>
                            </div>
                            <Button
                              size="sm"
                              onClick={() => assignEleveToGroupe(eleve.id, selectedGroupe.id)}
                              className="bg-[#285BBB]"
                            >
                              <UserPlus className="h-4 w-4 mr-1" />
                              Ajouter
                            </Button>
                          </div>
                        ))}
                        {eleves
                          .filter(e => e.groupe_id !== selectedGroupe.id)
                          .filter(e => {
                            if (!eleveSearchQuery) return true;
                            const search = eleveSearchQuery.toLowerCase();
                            return e.nom?.toLowerCase().includes(search) || 
                                   e.prenom?.toLowerCase().includes(search) ||
                                   e.email?.toLowerCase().includes(search);
                          }).length === 0 && (
                          <p className="text-[#6777A1] text-center py-4">
                            {eleveSearchQuery ? 'Aucun élève trouvé' : 'Tous les élèves sont déjà affectés'}
                          </p>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-col h-[500px]">
                      {/* Target Members Selector */}
                      {groupeMembers.length > 0 && (
                        <div className="p-3 border-b bg-gray-50">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm text-[#6777A1]">Envoyer à:</span>
                            <button
                              onClick={() => setTargetMembers([])}
                              className={`px-3 py-1 rounded-full text-sm transition-colors ${
                                targetMembers.length === 0 
                                  ? 'bg-[#285BBB] text-white' 
                                  : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                              }`}
                            >
                              Tout le groupe
                            </button>
                            {groupeMembers.map((member) => (
                              <button
                                key={member.id}
                                onClick={() => {
                                  if (targetMembers.includes(member.id)) {
                                    setTargetMembers(targetMembers.filter(id => id !== member.id));
                                  } else {
                                    setTargetMembers([...targetMembers, member.id]);
                                  }
                                }}
                                className={`px-3 py-1 rounded-full text-sm transition-colors ${
                                  targetMembers.includes(member.id) 
                                    ? 'bg-[#285BBB] text-white' 
                                    : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                                }`}
                              >
                                {member.prenom}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Messages */}
                      <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-[#f8fafc]">
                        {chatMessages.length === 0 ? (
                          <div className="text-center py-12">
                            <MessageCircle className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                            <p className="text-gray-500">Aucun message</p>
                            <p className="text-sm text-gray-400">Démarrez la discussion avec le groupe</p>
                          </div>
                        ) : (
                          chatMessages.map((msg, index) => {
                            const isFromMe = msg.sender_id === user?.id;
                            const showDate = index === 0 || 
                              new Date(msg.created_at).toDateString() !== new Date(chatMessages[index - 1].created_at).toDateString();
                            
                            return (
                              <React.Fragment key={msg.id}>
                                {showDate && (
                                  <div className="text-center my-4">
                                    <span className="text-xs text-gray-400 bg-white px-3 py-1 rounded-full shadow-sm">
                                      {formatDate(msg.created_at)}
                                    </span>
                                  </div>
                                )}
                                <motion.div
                                  initial={{ opacity: 0, y: 10 }}
                                  animate={{ opacity: 1, y: 0 }}
                                  className={`flex ${isFromMe ? 'justify-end' : 'justify-start'}`}
                                >
                                  <div className={`max-w-[75%]`}>
                                    {!isFromMe && (
                                      <p className="text-xs text-[#6777A1] mb-1 ml-1">{msg.sender_name}</p>
                                    )}
                                    <div
                                      className={`px-4 py-3 rounded-2xl ${
                                        isFromMe
                                          ? 'bg-[#285BBB] text-white rounded-br-md'
                                          : 'bg-white text-[#2C3C65] rounded-bl-md shadow-sm'
                                      }`}
                                    >
                                      {msg.target_members && msg.target_members.length > 0 && (
                                        <p className={`text-xs mb-1 ${isFromMe ? 'text-blue-200' : 'text-gray-400'}`}>
                                          Message privé
                                        </p>
                                      )}
                                      <p className="whitespace-pre-wrap break-words">{msg.content}</p>
                                    </div>
                                    <div className={`flex items-center gap-1 mt-1 ${isFromMe ? 'justify-end' : 'justify-start'}`}>
                                      <span className="text-xs text-gray-400">{formatTime(msg.created_at)}</span>
                                      {isFromMe && <CheckCheck className="h-3 w-3 text-blue-500" />}
                                    </div>
                                  </div>
                                </motion.div>
                              </React.Fragment>
                            );
                          })
                        )}
                        <div ref={messagesEndRef} />
                      </div>

                      {/* Input */}
                      <div className="p-4 border-t bg-white">
                        <div className="flex items-end gap-3">
                          <div className="flex-1">
                            <textarea
                              value={newMessage}
                              onChange={(e) => setNewMessage(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' && !e.shiftKey) {
                                  e.preventDefault();
                                  sendChatMessage();
                                }
                              }}
                              placeholder={targetMembers.length > 0 ? "Message privé..." : "Message au groupe..."}
                              className="w-full px-4 py-3 bg-gray-100 rounded-2xl resize-none focus:outline-none focus:ring-2 focus:ring-[#285BBB] max-h-32"
                              rows={1}
                            />
                          </div>
                          <Button
                            onClick={sendChatMessage}
                            disabled={sendingMessage || !newMessage.trim()}
                            className="bg-[#285BBB] hover:bg-[#2C3C65] rounded-full w-12 h-12 p-0"
                          >
                            {sendingMessage ? (
                              <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
                            ) : (
                              <Send className="h-5 w-5" />
                            )}
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-xl shadow-md p-12 text-center">
                <Users className="h-16 w-16 text-gray-200 mx-auto mb-4" />
                <p className="text-[#6777A1]">Sélectionnez un groupe pour voir les détails</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminGroupes;
